import type { LocaleCode } from '@/pages/cogo-marketing/i18n/locales'

export interface TemplateAuthoringCopy {
  noProject: string
  designPinned: string
  scan: string
  noSlots: string
  included: string
  excluded: string
  invalidTemplateId: string
  exporting: string
  exportPackage: string
  procedure: string
}

const COPY: Record<LocaleCode, TemplateAuthoringCopy> = {
  en: {
    noProject: 'No project is open.',
    designPinned:
      'This project has no design information, so the template will export with design rebinding locked (pinned).',
    scan: 'Scan slots',
    noSlots: 'No slots were found to turn into template content.',
    included: 'Included',
    excluded: 'Excluded',
    invalidTemplateId: 'templateId must match ^[a-z0-9][a-z0-9-]{0,63}$.',
    exporting: 'Exporting...',
    exportPackage: 'Export package',
    procedure:
      'Registry registration is complete only after the repository commit passes the Ajv and drift-lock gates.',
  },
  ko: {
    noProject: '열려 있는 프로젝트가 없어요',
    designPinned: '디자인 정보가 없어 디자인 재바인딩이 잠긴(pinned) 템플릿으로 내보내요',
    scan: '슬롯 스캔',
    noSlots: '템플릿화할 슬롯을 찾지 못했어요',
    included: '확인',
    excluded: '제외됨',
    invalidTemplateId: 'templateId는 ^[a-z0-9][a-z0-9-]{0,63}$ 형식이어야 해요',
    exporting: '내보내는 중...',
    exportPackage: '패키지 내보내기',
    procedure: '레지스트리 등록은 저장소 커밋 + Ajv/드리프트락 게이트를 통과해야 완료됩니다',
  },
  ja: {
    noProject: '開いているプロジェクトがありません',
    designPinned:
      'デザイン情報がないため、デザインの再バインドを固定（pinned）したテンプレートとして書き出します',
    scan: 'スロットをスキャン',
    noSlots: 'テンプレート化できるスロットが見つかりませんでした',
    included: '含める',
    excluded: '除外済み',
    invalidTemplateId: 'templateId は ^[a-z0-9][a-z0-9-]{0,63}$ の形式で入力してください',
    exporting: '書き出し中...',
    exportPackage: 'パッケージを書き出す',
    procedure:
      'レジストリへの登録は、リポジトリへのコミットが Ajv とドリフトロックのゲートを通過すると完了します',
  },
  // Studio's existing locale registry normalizes zh-CN to `zh`.
  zh: {
    noProject: '当前没有打开的项目',
    designPinned: '没有设计信息，将导出为设计重新绑定已锁定（pinned）的模板',
    scan: '扫描插槽',
    noSlots: '未找到可模板化的插槽',
    included: '已包含',
    excluded: '已排除',
    invalidTemplateId: 'templateId 必须符合 ^[a-z0-9][a-z0-9-]{0,63}$ 格式',
    exporting: '正在导出...',
    exportPackage: '导出包',
    procedure: '仓库提交通过 Ajv 和漂移锁定门禁后，才算完成注册表登记',
  },
  'zh-TW': {
    noProject: '目前沒有開啟的專案',
    designPinned: '沒有設計資訊，將匯出為設計重新綁定已鎖定（pinned）的範本',
    scan: '掃描插槽',
    noSlots: '找不到可範本化的插槽',
    included: '已包含',
    excluded: '已排除',
    invalidTemplateId: 'templateId 必須符合 ^[a-z0-9][a-z0-9-]{0,63}$ 格式',
    exporting: '正在匯出...',
    exportPackage: '匯出套件',
    procedure: '儲存庫提交通過 Ajv 與漂移鎖定閘門後，才算完成登錄檔註冊',
  },
}

export function getTemplateAuthoringCopy(locale: LocaleCode): TemplateAuthoringCopy {
  return COPY[locale] ?? COPY.en
}
